import React, { useState, useEffect } from "react";
import {
  Box,
  Button,
  HStack,
  Text,

  Center,
  Select,
  Portal,
  Table,
  Badge,
} from "@chakra-ui/react";
import { useDispatch, useSelector } from "react-redux";
import { createListCollection } from "@chakra-ui/react";
import { getRecentTransactionsData } from "../Redux/Action";
import { useAppTheme } from "../Hooks/useAppTheme";

const STATUS_COLORS = {
  accepted: { bg: "green.100", text: "green.800" },
  rejected: { bg: "red.100", text: "red.800" },
  pending: { bg: "yellow.100", text: "yellow.800" },
  success: { bg: "blue.100", text: "blue.800" },
  cancelled: { bg: "gray.100", text: "gray.800" },
};

const STATIC_STATUS_OPTIONS = ["all", "accepted", "rejected", "pending", "success", "cancelled"];
const STATIC_ACTION_OPTIONS = ["all", "deposit", "withdrawal"];

export default function Spinner() {
  return (
    <div className="flex justify-center items-center h-32">
      <div className="w-12 h-12 border-4 border-blue-500 border-t-transparent rounded-full animate-spin"></div>
    </div>
  );
}

export const RecentTransactions = ({ defaultSet }) => {
  const theme = useAppTheme();
  const loading = useSelector((state) => state?.GetTransactions?.loading);
  const dispatch = useDispatch();
  const [transactions, setTransactions] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [selectedStatus, setSelectedStatus] = useState("all");
  const [selectedAction, setSelectedAction] = useState();
  const [rowCount] = useState(10);
  const [totalPages, setTotalPages] = useState(1);
  useEffect(() => {
    setSelectedAction(defaultSet || ["all"]);
  }, [defaultSet]);
  const fetchTransactions = () => {
    const payload = {
      currentPage,
      rowCount,
      status: selectedStatus !== "all" ? selectedStatus[0] || selectedStatus : null,
      action: selectedAction !== "all"
        ? Array.isArray(selectedAction) ? selectedAction[0] : selectedAction
        : null
    };

    dispatch(
      getRecentTransactionsData({
        payload,
        callback: (data) => {
          if (data?.meta?.code === 200) {
            setTransactions(data?.data || []);
            setTotalPages(data?.meta?.totalPages || 1);
          }
        },
      })
    );
  };

  useEffect(() => {
    fetchTransactions();
  }, [currentPage, selectedStatus, selectedAction]);

  const statusCollections = createListCollection({
    items: STATIC_STATUS_OPTIONS.map((status) => ({
      label: status === "all" ? "All Status" : status.charAt(0).toUpperCase() + status.slice(1),
      value: status,
    })),
  });

  const actionCollections = createListCollection({
    items: STATIC_ACTION_OPTIONS.map((action) => ({
      label: action === "all" ? "All Actions" : action.charAt(0).toUpperCase() + action.slice(1),
      value: action,
    })),
  });


  return (
    <Box
      w="100%"
      maxW="100%"
      mx="auto"
      p={6}
      rounded="xl"
      boxShadow="base"
      style={{ backgroundColor: theme.bgCard, color: theme.textPrimary }}
    >
      <Text fontSize="sm" fontWeight="semibold" mb={4} style={{ color: theme.textPrimary }}>
        Recent Transactions
      </Text>

      {loading ? (
        <Spinner />

      ) : (
        <>
          <HStack
            mb={4}
            spacing={4}
            alignItems="center"
            width="100%"
            flexWrap="wrap"
            justifyContent="space-between"
          >
            <Box width={{ base: "100%", md: "30%" }}>
              <Select.Root
                value={selectedStatus}
                onValueChange={(e) => setSelectedStatus(e?.value)}
                size="md"
                collection={statusCollections}
              >
                <Select.Control
                  border="1px solid"
                  borderColor={theme.borderPrimary}
                  borderRadius="md"
                  py={1}
                  px={2}
                  fontSize="xs"
                  bg={theme.inputBg}
                  color={theme.inputText}
                >
                  <Select.Trigger>
                    <Select.ValueText placeholder="Select Status" />
                  </Select.Trigger>
                  <Select.IndicatorGroup>
                    <Select.Indicator />
                  </Select.IndicatorGroup>
                </Select.Control>
                <Portal>
                  <Select.Positioner>
                    <Select.Content bg={theme.bgCard} color={theme.textPrimary} borderColor={theme.borderPrimary}>
                      {statusCollections.items.map((item) => (
                        <Select.Item key={item.value} item={item} _hover={{ bg: theme.bgSecondary }}>
                          {item.label}
                          <Select.ItemIndicator />
                        </Select.Item>
                      ))}
                    </Select.Content>
                  </Select.Positioner>
                </Portal>
              </Select.Root>
            </Box>

            <Box width={{ base: "100%", md: "30%" }}>
              <Select.Root
                value={selectedAction}
                onValueChange={(e) => setSelectedAction(e?.value)}
                size="md"
                collection={actionCollections}
              >
                <Select.Control
                  border="1px solid"
                  borderColor={theme.borderPrimary}
                  borderRadius="md"
                  py={1}
                  px={2}
                  fontSize="xs"
                  bg={theme.inputBg}
                  color={theme.inputText}
                >
                  <Select.Trigger>
                    <Select.ValueText placeholder="Select Action" />
                  </Select.Trigger>
                  <Select.IndicatorGroup>
                    <Select.Indicator />
                  </Select.IndicatorGroup>
                </Select.Control>
                <Portal>
                  <Select.Positioner>
                    <Select.Content bg={theme.bgCard} color={theme.textPrimary} borderColor={theme.borderPrimary}>
                      {actionCollections.items.map((item) => (
                        <Select.Item key={item.value} item={item} _hover={{ bg: theme.bgSecondary }}>
                          {item.label}
                          <Select.ItemIndicator />
                        </Select.Item>
                      ))}
                    </Select.Content>
                  </Select.Positioner>
                </Portal>
              </Select.Root>
            </Box>

            <Box width={{ base: "100%", md: "30%" }}>
              <Button
                bg={theme.buttonSecondaryBg}
                color={theme.buttonSecondaryText}
                border={`1px solid ${theme.buttonSecondaryBorder}`}
                size="sm"
                fontSize="xs"
                transition="all 0.2s ease"
                _hover={{ opacity: 0.9 }}
                onClick={() => {
                  setSelectedStatus(["all"]);
                  setSelectedAction(["all"]);
                  setCurrentPage(1);
                }}
                w="100%"
              >
                Reset Filters
              </Button>
            </Box>
          </HStack>

          {/* 🔧 Fixed Height Wrapper */}
          <Box
            overflowX="auto"
            minHeight={{ base: "300px", md: "380px" }} // 👈 Adjust height responsively
            maxHeight="100%"
          >
            <Table.Root size="sm" variant="unstyled">
              <Table.Header>
                <Table.Row bg={theme.bgSecondary} height="40px">
                  <Table.ColumnHeader fontSize="xs" style={{ color: theme.textPrimary }}>S No.</Table.ColumnHeader>
                  <Table.ColumnHeader fontSize="xs" style={{ color: theme.textPrimary }}>Amount</Table.ColumnHeader>
                  <Table.ColumnHeader fontSize="xs" style={{ color: theme.textPrimary }}>Status</Table.ColumnHeader>
                  <Table.ColumnHeader fontSize="xs" style={{ color: theme.textPrimary }}>Method</Table.ColumnHeader>
                  <Table.ColumnHeader fontSize="xs" style={{ color: theme.textPrimary }}>Date and Time</Table.ColumnHeader>
                  <Table.ColumnHeader fontSize="xs" style={{ color: theme.textPrimary }}>Transaction Type</Table.ColumnHeader>
                </Table.Row>
              </Table.Header>
              <Table.Body>
                {transactions.length > 0 ? (
                  transactions.map((item, index) => {
                    const serialNumber = (currentPage - 1) * rowCount + index + 1;

                    return (
                      <Table.Row
                        key={item._id}
                        borderBottom="1px solid"
                        borderColor={theme.borderPrimary}
                        _hover={{ bg: theme.bgSecondary }}
                        height="38px"
                      >
                        <Table.Cell fontSize="xs" py={1} style={{ color: theme.textPrimary }}>
                          {serialNumber}
                        </Table.Cell>
                        <Table.Cell fontSize="xs" py={1} style={{ color: theme.textPrimary }}>
                          {item.amount}
                        </Table.Cell>
                        <Table.Cell fontSize="xs" py={1} style={{ color: theme.textPrimary }}>
                          <Badge
                            px={2}
                            py={1}
                            borderRadius="md"
                            backgroundColor={STATUS_COLORS[item.status?.toLowerCase()]?.bg || theme.bgTertiary}
                            color={STATUS_COLORS[item.status?.toLowerCase()]?.text || theme.textPrimary}
                            fontSize="xs"
                          >
                            {item.status}
                          </Badge>
                        </Table.Cell>
                        <Table.Cell fontSize="xs" py={1} style={{ color: theme.textPrimary }}>
                          {item.method}
                        </Table.Cell>
                        <Table.Cell fontSize="xs" py={1} style={{ color: theme.textPrimary }}>
                          {item.createdAt}
                        </Table.Cell>
                        <Table.Cell fontSize="xs" py={1} style={{ color: theme.textPrimary }}>
                          {item.action}
                        </Table.Cell>
                      </Table.Row>
                    );
                  })
                ) : (
                  <Table.Row>
                    <Table.Cell colSpan="6" textAlign="center" fontSize="xs" py={2} style={{ color: theme.textSecondary }}>
                      No Transactions Found
                    </Table.Cell>
                  </Table.Row>
                )}
              </Table.Body>
            </Table.Root>
          </Box>

          <HStack justify="space-between" spacing={4} mt={4} flexWrap="wrap">
            <Text fontSize="xs" style={{ color: theme.textSecondary }}>{`Page ${currentPage} of ${totalPages}`}</Text>
            <HStack spacing={4}>
              <Button
                onClick={() => setCurrentPage((prev) => Math.max(prev - 1, 1))}
                isDisabled={currentPage === 1}
                bg={currentPage === 1 ? theme.buttonDisabledBg : theme.buttonPrimaryBg}
                color={currentPage === 1 ? theme.buttonDisabledText : theme.buttonPrimaryText}
                size="xs"
                fontSize="xs"
                padding={5}
              >
                Prev
              </Button>
              <Text fontSize="xs" style={{ color: theme.textPrimary }}>{currentPage}</Text>
              <Button
                onClick={() => setCurrentPage((prev) => Math.min(prev + 1, totalPages))}
                isDisabled={currentPage === totalPages}
                bg={currentPage === totalPages ? theme.buttonDisabledBg : theme.buttonPrimaryBg}
                color={currentPage === totalPages ? theme.buttonDisabledText : theme.buttonPrimaryText}
                size="xs"
                fontSize="xs"
                padding={5}
              >
                Next
              </Button>
            </HStack>
          </HStack>
        </>
      )}
    </Box>
  );
};
